import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { deepClone, getFromServerTopic } from "@utility/slice";
import { IDLE, LOADING } from "@constants";
import { WELLSTAT_IAQ_INDEX_NAME } from "@src/iaqIndex/constants.js";
import { addOrRemove } from "@helpers/collections";
import { INSTALL_CATEGORIES } from "@src/iaq/constants";

const SLICE = "iaqIndex";

const action = (name) => `${SLICE}/${name}`;

const getFromServer = (action, contextOptionsFn, payloadFn) =>
  getFromServerTopic({ topic: SLICE, action, contextOptionsFn, payloadFn });

export const getIAQIndexes = getFromServer("getIAQIndexes", async ({ arg }) => {
  return arg;
});

const getBuildingIndexes = getFromServer(
  "getBuildingIndexes",
  async ({ arg }) => {
    return arg;
  },
);

const getBuildings = createAsyncThunk(
  action("getBuildings"),
  async (payload, { getState }) => {
    const { lookupdata } = getState().persistedAppData;
    return lookupdata.buildings;
  },
);

export const initializePage = createAsyncThunk(
  action("initializePage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getBuildings()),
      dispatch(getBuildingIndexes()),
    ]);
  },
);

export const assignIndex = getFromServer("assignIndex", async ({ arg }) => arg);

export const assignIndexToBuildings = createAsyncThunk(
  "iaqIndex/assignIndexToBuildings",
  async (arg, { dispatch }) => {
    await dispatch(assignIndex(arg));
    return dispatch(initializePage());
  },
);

export const saveIndex = getFromServer(
  "saveIndex",
  async ({ arg }) => {
    const { id, name } = arg;
    return { id, name };
  },
  async (arg) => {
    return arg;
  },
);

export const slice = createSlice({
  name: SLICE,
  initialState: {
    iaqIndexes: undefined,
    status: LOADING,
  },
  reducers: {
    createIndex: (state) => {
      const freshIndex = {
        ...deepClone(
          state.iaqIndexes.find((it) => it.name === WELLSTAT_IAQ_INDEX_NAME),
        ),
        id: null,
        installCategories: [...INSTALL_CATEGORIES],
        isReadOnly: false,
        name: "",
      };
      freshIndex.values.forEach((it) => (it.id = null));
      state.selectedIndex = freshIndex;
    },
    selectIndex: (state, action) => {
      state.selectedIndex = deepClone(action.payload);
    },
    toggleInstallCategory: (state, action) => {
      state.selectedIndex.installCategories = [
        ...addOrRemove(
          new Set(state.selectedIndex.installCategories),
          action.payload,
        ),
      ];
    },
    updateName: (state, action) => {
      state.selectedIndex.name = action.payload;
    },
    updateSensorIsActive: (state, action) => {
      const { sensor, isActive } = action.payload;
      const newValues = [];
      for (const v of state.selectedIndex.values) {
        if (v.sensor !== sensor) {
          newValues.push(v);
          continue;
        }

        newValues.push({
          ...v,
          isActive,
        });
      }

      state.selectedIndex = {
        ...state.selectedIndex,
        values: newValues,
      };
    },
    updateSensorThresholds: (state, action) => {
      const { sensor, chartThresholdMin, chartThresholdMax } = action.payload;
      const newValues = [];
      for (const v of state.selectedIndex.values) {
        if (v.sensor !== sensor) {
          newValues.push(v);
          continue;
        }

        newValues.push({
          ...v,
          chartThresholdMin,
          chartThresholdMax,
        });
      }

      state.selectedIndex = {
        ...state.selectedIndex,
        values: newValues,
      };
    },
    updateSensorValues: (state, action) => {
      const { sensor, values } = action.payload;
      const newValues = [];
      for (const v of state.selectedIndex.values) {
        if (v.sensor !== sensor) {
          newValues.push(v);
          continue;
        }

        let acceptableLowerThreshold = undefined;
        let goodLowerThreshold = undefined;
        let greatLowerThreshold = undefined;
        let i = 0;
        if (values.length > 3) {
          acceptableLowerThreshold = values[i++];
          goodLowerThreshold = values[i++];
          greatLowerThreshold = values[i++];
        }
        const greatThreshold = values[i++];
        const goodThreshold = values[i++];
        const acceptableThreshold = values[i++];

        newValues.push({
          ...v,
          acceptableLowerThreshold,
          goodLowerThreshold,
          greatLowerThreshold,
          greatThreshold,
          goodThreshold,
          acceptableThreshold,
        });
      }

      state.selectedIndex = {
        ...state.selectedIndex,
        values: newValues,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBuildings.fulfilled, (state, action) => {
        const buildingsById = {};
        action.payload.forEach((it) => (buildingsById[it.id] = it));
        state.buildingsById = buildingsById;
      })
      .addCase(getBuildingIndexes.fulfilled, (state, action) => {
        const { payload } = action;
        const assignedIndexByBuildingId = {};

        payload.response.forEach((it) => {
          assignedIndexByBuildingId[it.buildingId] = it;
        });
        state.assignedIndexByBuildingId = assignedIndexByBuildingId;
      })
      .addCase(getIAQIndexes.fulfilled, (state, action) => {
        const { payload } = action;
        state.iaqIndexes = payload.response;
      })
      .addCase(initializePage.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(initializePage.fulfilled, (state) => {
        state.status = IDLE;
      })
      .addCase(saveIndex.fulfilled, (state, action) => {
        const { payload } = action;
        const newIndex = payload.response;

        if (!newIndex) return;

        const isUpdate = state.selectedIndex.id;
        state.selectedIndex = newIndex;

        if (!isUpdate) {
          state.iaqIndexes = [...state.iaqIndexes, newIndex];
          return;
        }

        const newIndexes = [];
        for (const i of state.iaqIndexes) {
          const indexToPush = i.name === newIndex.name ? newIndex : i;
          newIndexes.push(indexToPush);
        }
      });
  },
});

export const {
  createIndex,
  selectIndex,
  toggleInstallCategory,
  updateName,
  updateSensorIsActive,
  updateSensorThresholds,
  updateSensorValues,
} = slice.actions;

export const INITIAL_STATE = slice.getInitialState();

export default slice.reducer;
